import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import type { File } from '@actual-app/core/types/file';

import { Boton } from '#components/mobile/ui/Boton';
import { HojaAcciones } from '#components/mobile/ui/HojaAcciones';
import type { FilaAccion } from '#components/mobile/ui/HojaAcciones';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';

import {
  claveArchivo,
  compartidoCon,
  duenoEsServidor,
  nombreDueno,
} from './datos';
import { Galeon } from './Galeon';
import { coloresBarco, guardarPortada, PORTADAS, reducirFoto } from './portada';
import type { Portada } from './portada';

type HojaPresupuestoProps = {
  file: File;
  currentUserId: string;
  esAdmin: boolean;
  portada: Portada | null;
  onCerrar: () => void;
  onAbrir: () => void;
  onRenombrar: (nombre: string) => Promise<void>;
  onPortada: (portada: Portada) => void;
  onCompartir: () => void;
  /** Devuelve el texto del error, o null si salió bien. */
  onPasarAMiCuenta: () => Promise<string | null>;
  onBorrar: () => void;
};

type Modo = 'acciones' | 'renombrar' | 'portada';

/** Menú «…» de un velero del puerto. */
export function HojaPresupuesto({
  file,
  currentUserId,
  esAdmin,
  portada,
  onCerrar,
  onAbrir,
  onRenombrar,
  onPortada,
  onCompartir,
  onPasarAMiCuenta,
  onBorrar,
}: HojaPresupuestoProps) {
  const { t } = useTranslation();
  const [modo, setModo] = useState<Modo>('acciones');
  const [nombre, setNombre] = useState(file.name);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const entradaFoto = useRef<HTMLInputElement>(null);
  const clave = claveArchivo(file);
  const enNube = 'cloudFileId' in file && Boolean(file.cloudFileId);
  const esDueno = file.owner === currentUserId;
  const puedeCompartir =
    enNube && file.state !== 'broken' && (esDueno || esAdmin);
  const puedeQuedarselo = enNube && esAdmin && duenoEsServidor(file);
  const con = compartidoCon(file, currentUserId);

  const elegir = (nueva: Portada) => {
    if (guardarPortada(clave, nueva)) {
      onPortada(nueva);
      setAviso(null);
    } else {
      setAviso(t('Could not save the cover on this device.'));
    }
  };

  const guardarNombre = async () => {
    const limpio = nombre.trim();
    if (!limpio || limpio === file.name) {
      setModo('acciones');
      return;
    }
    setGuardando(true);
    await onRenombrar(limpio);
    setGuardando(false);
    setModo('acciones');
  };

  const filas: FilaAccion[] = [
    { id: 'abrir', texto: t('Open'), icono: 'anchor', onPress: onAbrir },
    {
      id: 'renombrar',
      texto: t('Rename'),
      icono: 'pencil',
      onPress: () => setModo('renombrar'),
    },
    {
      id: 'portada',
      texto: t('Cover image'),
      icono: 'sparkle',
      onPress: () => setModo('portada'),
    },
  ];
  if (puedeCompartir) {
    filas.push({
      id: 'compartir',
      texto: t('Share'),
      icono: 'users',
      detalle: con.length > 0 ? con.join(', ') : undefined,
      onPress: onCompartir,
    });
  }
  if (puedeQuedarselo) {
    filas.push({
      id: 'pasar',
      texto: t('Move to my account'),
      icono: 'user',
      detalle: t('Its owner is still the old server account'),
      onPress: () => {
        setAviso(null);
        void onPasarAMiCuenta().then(error => {
          if (error) {
            setAviso(error);
          }
        });
      },
    });
  }
  filas.push({
    id: 'borrar',
    texto: t('Delete'),
    icono: 'trash',
    peligro: true,
    onPress: onBorrar,
  });

  const colores = coloresBarco(clave, portada);
  const de = nombreDueno(file, currentUserId);
  const subtitulo = [
    esDueno ? t('Yours') : de ? t('From {{name}}', { name: de }) : null,
    con.length > 0
      ? t('shared with {{names}}', { names: con.join(', ') })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <HojaAcciones
      data-testid="hoja-presupuesto"
      titulo={file.name}
      subtitulo={subtitulo || undefined}
      filas={filas}
      onCerrar={onCerrar}
    >
      {modo === 'renombrar' ? (
        <View style={{ gap: 10, paddingTop: 4 }}>
          <Input
            autoFocus
            aria-label={t('Budget name')}
            data-testid="presupuesto-nombre"
            value={nombre}
            onChangeValue={setNombre}
            onEnter={() => void guardarNombre()}
            style={{ minHeight: TACTIL, fontSize: 16 }}
          />
          <Boton
            bloque
            data-testid="presupuesto-guardar-nombre"
            isDisabled={!nombre.trim() || guardando}
            onPress={() => void guardarNombre()}
          >
            <Trans>Save</Trans>
          </Boton>
        </View>
      ) : modo === 'portada' ? (
        <View style={{ gap: 14, paddingTop: 4 }}>
          <View style={{ alignItems: 'center' }}>
            <Galeon {...colores} width={130} />
          </View>
          <View
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
            }}
          >
            {PORTADAS.map(p => {
              const elegida =
                portada?.tipo === 'predefinida'
                  ? portada.id === p.id
                  : !portada && coloresBarco(clave, null).vela === p.vela;
              return (
                <Button
                  key={p.id}
                  variant="bare"
                  aria-label={t('Colour {{name}}', { name: p.id })}
                  aria-pressed={elegida}
                  data-testid={`portada-${p.id}`}
                  onPress={() => elegir({ tipo: 'predefinida', id: p.id })}
                  style={{
                    aspectRatio: '1',
                    borderRadius: radio.sm,
                    background: `linear-gradient(160deg, ${p.vela} 0 50%, ${p.casco} 50% 82%, ${p.bandera} 82%)`,
                    outline: elegida ? `2px solid ${color.accent}` : 'none',
                    outlineOffset: 2,
                  }}
                />
              );
            })}
            <Button
              variant="bare"
              data-testid="portada-foto"
              onPress={() => entradaFoto.current?.click()}
              style={{
                aspectRatio: '1',
                borderRadius: radio.sm,
                backgroundColor: color.surface2,
                color: color.fg2,
                fontSize: 11,
                fontWeight: 700,
                outline:
                  portada?.tipo === 'foto'
                    ? `2px solid ${color.accent}`
                    : 'none',
                outlineOffset: 2,
              }}
            >
              <Trans>Your photo</Trans>
            </Button>
          </View>
          <input
            ref={entradaFoto}
            type="file"
            accept="image/*"
            hidden
            onChange={async e => {
              const archivo = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (!archivo) {
                return;
              }
              try {
                elegir({ tipo: 'foto', datos: await reducirFoto(archivo) });
              } catch {
                setAviso(t('Could not read that photo.'));
              }
            }}
          />
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <Trans>The cover is saved on this device.</Trans>
          </Text>
          {aviso && (
            <Text
              role="alert"
              style={{ ...texto.secundario, color: color.bad }}
            >
              {aviso}
            </Text>
          )}
          <Boton bloque variante="fantasma" onPress={() => setModo('acciones')}>
            <Trans>Done</Trans>
          </Boton>
        </View>
      ) : aviso ? (
        <Text role="alert" style={{ ...texto.secundario, color: color.bad }}>
          {aviso}
        </Text>
      ) : undefined}
    </HojaAcciones>
  );
}
